import React, { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Grid, Paper, Typography, Button } from '@material-ui/core';

import { ImageViewer } from 'components/image-viewer/image-viewer';
import { ArchivalUnitCopy } from 'types';
import { ReadOnlyField } from 'components/read-only-field/read-only-field';
import { RegisterField } from 'modules/register/register-field';
import { handleOriginatorLink } from 'modules/register/reqister-utils';
import { StructMap } from 'components/struct-map/struct-map';
import { LinkField } from 'components/link-field/link-field';
import { AccessibleDigitalContent } from 'modules/archival-unit-private/archival-unit-detail';
import { useStructMapImages } from 'components/struct-map/stuct-map-images-hook';
import { StructMapImageInfo } from 'components/struct-map/struct-map-image-info';
import { findValidDescribingAid } from 'components/grid/v2/context/hierarchy-context';
import { OrderCtx } from 'components/order-ctx/order-ctx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },
    paper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
    archivalUnitInfo: {
      marginTop: 10,
      fontWeight: 400,
      color: theme.palette.primary.main,
    },
    content: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
  })
);

export const ArchivalUnitFields = ({ item }: { item?: ArchivalUnitCopy }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const {
    numericId,
    archivalUnitReference,
    summary,
    originators,
    structMap,
    aipData = [],
    zipFile,
  } = item || {};

  const {
    archivalCollection,
    entityCharacter,
    timeRange,
    accessType,
    structuredLink,
    evidenceTypeName,
    note,
    storageUnits = [],
    describingArchivalAids = [],
  } = archivalUnitReference || {};

  const {
    onLeafClick,
    imageViewerData,
    defaultIndex,
    toolbarButtons,
  } = useStructMapImages(structMap, item);
  const { isArchivalUnitInOrder } = useContext(OrderCtx);
  const isCurrentArchivalUnitInCart =
    isArchivalUnitInOrder?.(numericId) ?? false;

  const archivalAid = findValidDescribingAid(describingArchivalAids);

  const downloadZippedContent = useCallback(async () => {
    window.open(
      process.env.PUBLIC_URL + `/api/file/archival/${zipFile?.id}`,
      '_blank'
    );
  }, [zipFile]);

  return (
    <>
      <Typography variant="h5">{summary}</Typography>
      {entityCharacter === 'DIGITAL' && aipData.length > 0 && (
        <p className={classes.archivalUnitInfo}>
          Toto je digitální archiválie, její plný obsah je zpřístupněný,
          archviálii nelze objednat.
        </p>
      )}
      {aipData.length > 0 && isCurrentArchivalUnitInCart && (
        <p className={classes.archivalUnitInfo}>
          Tato archiválie je digitalizovaná a veřejně přístupná. Pokud se
          rozhodnete objednat její analogový originál, uveďte důvod proč vám
          nestačí její digitální podoba.
        </p>
      )}
      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <Typography variant="h6">
            {formatMessage({
              id: 'archivalUnit.inclusion',
            })}
          </Typography>
          <br />
          <br />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollection',
            })}
            value={archivalCollection?.name}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollectionNumber',
            })}
            value={archivalCollection?.evidenceNumber}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          {archivalAid && (
            <LinkField
              label={formatMessage({
                id: 'archivalUnit.archivalAid',
              })}
              value={archivalAid?.name}
              href={`pomucky/0/${archivalAid?.id}`}
            />
          )}
        </Grid>
      </Paper>
      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit',
            })}
            value={numericId?.toString()}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.regest',
            })}
            value={summary}
            alwaysShow={true}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.referenceDesignation',
            })}
            value={structuredLink}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.type',
            })}
            value={evidenceTypeName}
          />
          <ReadOnlyField
            label={formatMessage({ id: 'archivalUnit.form' })}
            value={formatMessage({
              id: `archivalUnit.entityCharacter.${entityCharacter}`,
            })}
          />
          <ReadOnlyField
            label={formatMessage({ id: 'archivalUnit.digitalization' })}
            Component={
              <AccessibleDigitalContent
                hasDigitalContent={
                  entityCharacter === 'DIGITAL' || entityCharacter === 'HYBRID'
                }
                structMapData={structMap}
              />
            }
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.dating',
            })}
            value={timeRange}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.storageUnit',
            })}
            value={
              storageUnits.length > 0
                ? storageUnits.map((s) => s).join(', ')
                : undefined
            }
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.accessibility',
            })}
            value={formatMessage({
              id: `archivalUnit.accessType.${accessType}`,
              defaultMessage: `${accessType}`,
            })}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.note',
            })}
            value={note}
          />
        </Grid>
      </Paper>
      {originators && (
        <Paper elevation={0} className={classes.paper}>
          <Grid container className={classes.root}>
            <Typography variant="h6">
              {formatMessage({
                id: 'archivalUnit.indexEntries',
              })}
            </Typography>
            <br />
            <br />
            {originators?.map?.((o, i) => (
              <Grid item xs={12} key={o?.id}>
                <RegisterField
                  key={o?.id}
                  label={
                    i === 0
                      ? formatMessage({
                          id: 'archivalUnit.originator',
                        })
                      : ''
                  }
                  withoutBorder={true}
                  value={o?.label}
                  valueLink={o && handleOriginatorLink(o, true)}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {structMap !== undefined && (
        <Paper elevation={0} className={classes.paper}>
          <ImageViewer
            defaultIndex={defaultIndex}
            allData={imageViewerData}
            toolbarButtons={toolbarButtons}
            InfoComponent={StructMapImageInfo}
            customProps={{
              parentEntity: item,
            }}
          />
        </Paper>
      )}

      {structMap !== undefined && (
        <Paper elevation={0} className={classes.paper} id="digital-content">
          <Grid container className={classes.root}>
            <div className={classes.content}>
              <Typography variant="h6">
                {formatMessage({ id: 'archivalUnit.content' })}
              </Typography>
              {zipFile && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={downloadZippedContent}
                >
                  {formatMessage({
                    id: 'archivalUnit.downloadAllContent',
                  })}
                </Button>
              )}
            </div>
            <StructMap
              data={structMap}
              onChange={() => {}}
              onLeafClick={onLeafClick}
              parentEntity={item!}
            />
          </Grid>
        </Paper>
      )}
    </>
  );
};
